// #ux-redesign: chip de adjunto unificado.
// Reemplaza el "boton azul" legacy (BtnFile / BtnAttach). Dos modos:
//
//   1) `file` (modo redaccion): se le pasa una instancia File local. Genera
//      el thumb via URL.createObjectURL para imagenes. Muestra X de remover.
//
//   2) `fileId` (modo lectura): se le pasa el _id del file en MultimediaService.
//      Fetchea metadata (name, size, mimetype). Para imagenes hace fetch del
//      blob para mostrar el thumb. Click → download.
//
// Diseño: card horizontal compacta, 56px de alto. Para imagenes el thumb
// ocupa el lado izquierdo (square). Para otros tipos, icono FA coloreado
// segun categoria.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslate } from 'react-translate';
import { useSelector } from 'react-redux';
import MultimediaService from 'services/MultimediaService';
import nativeSender from 'helpers/nativeSender';

const IMAGE_RE = /^image\//;
const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|bmp|tiff|svg)$/i;

const getFileKind = (mime = '', name = '') => {
  if (IMAGE_RE.test(mime) || IMAGE_EXT_RE.test(name)) return 'image';
  if (mime === 'application/pdf' || /\.pdf$/i.test(name)) {
    return { kind: 'pdf', icon: 'fa-file-pdf', color: '#dc2626' };
  }
  if (mime.includes('word') || /\.(docx?|odt|rtf)$/i.test(name)) {
    return { kind: 'word', icon: 'fa-file-word', color: '#2563eb' };
  }
  if (mime.includes('excel') || mime.includes('spreadsheet') || /\.(xlsx?|ods|csv)$/i.test(name)) {
    return { kind: 'excel', icon: 'fa-file-excel', color: '#16a34a' };
  }
  if (mime.includes('powerpoint') || mime.includes('presentation') || /\.(pptx?|odp)$/i.test(name)) {
    return { kind: 'ppt', icon: 'fa-file-powerpoint', color: '#ea580c' };
  }
  if (/^audio\//.test(mime) || /\.(mp3|wav|flac|aiff|ogg)$/i.test(name)) {
    return { kind: 'audio', icon: 'fa-file-audio', color: '#a855f7' };
  }
  if (/^video\//.test(mime) || /\.(mp4|mov|avi|mkv|webm)$/i.test(name)) {
    return { kind: 'video', icon: 'fa-file-video', color: '#0891b2' };
  }
  if (mime.includes('zip') || /\.(zip|rar|7z|tar|gz)$/i.test(name)) {
    return { kind: 'archive', icon: 'fa-file-archive', color: '#ca8a04' };
  }
  if (/^text\//.test(mime) || /\.(txt|log|md|json|xml)$/i.test(name)) {
    return { kind: 'text', icon: 'fa-file-alt', color: '#6b7280' };
  }
  return { kind: 'unknown', icon: 'fa-file', color: '#6b7280' };
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let u = 0;
  while (n >= 1024 && u < units.length - 1) {
    n /= 1024;
    u += 1;
  }
  return `${n.toFixed(n >= 10 || u === 0 ? 0 : 1)} ${units[u]}`;
};

const AttachmentChip = ({ file, fileId, onRemove, compact }) => {
  const ta = useTranslate('a11y');
  const isLocal = !!file;
  const { nativeWebView } = useSelector((s) => s.commonReducer);

  // Modo local (File): metadata viene del File. Thumb via createObjectURL.
  const localThumb = useMemo(() => {
    if (!isLocal) return null;
    if (file && file.type && IMAGE_RE.test(file.type)) {
      return URL.createObjectURL(file);
    }
    return null;
  }, [file, isLocal]);

  // Cleanup del object URL al desmontar.
  useEffect(() => {
    return () => {
      if (localThumb) URL.revokeObjectURL(localThumb);
    };
  }, [localThumb]);

  // Modo remoto (fileId): fetch metadata + thumb si es imagen.
  const [meta, setMeta] = useState(null);
  const [remoteThumb, setRemoteThumb] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (remoteThumb) URL.revokeObjectURL(remoteThumb);
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isLocal || !fileId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await MultimediaService.getFileInfo(fileId);
        if (cancelled || !mountedRef.current) return;
        setMeta(data || {});
        // Fetch thumb solo si es imagen
        const name = data?.name || '';
        const mime = data?.mimetype || '';
        if (IMAGE_RE.test(mime) || IMAGE_EXT_RE.test(name)) {
          const url = await MultimediaService.getFile(fileId);
          if (!cancelled && mountedRef.current && url) setRemoteThumb(url);
        }
      } catch (e) {
        // silent: el chip cae a icono generico
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fileId, isLocal]);

  const name = isLocal ? file?.name || 'file' : meta?.name || '';
  const mime = isLocal ? file?.type || '' : meta?.mimetype || '';
  const size = isLocal ? file?.size : meta?.size;
  const kindInfo = getFileKind(mime, name);
  const isImage = kindInfo === 'image';
  const thumb = isLocal ? localThumb : remoteThumb;

  const handleDownload = async () => {
    if (isLocal || !fileId) return;
    const fileName = name || 'Attachment';
    if (nativeWebView) {
      return nativeSender.send('DOWNLOAD_BLOB', { id: fileId, name: fileName });
    }
    const url = await MultimediaService.getFile(fileId);
    if (!url) return;
    if (!window.navigator.msSaveOrOpenBlob) {
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else {
      window.navigator.msSaveOrOpenBlob(url, fileName);
    }
  };

  // No renderizar hasta tener metadata (modo remoto).
  if (!isLocal && !meta) {
    return (
      <div className={`attach-chip attach-chip--loading${compact ? ' attach-chip--compact' : ''}`} aria-busy="true">
        <div className="attach-chip__media attach-chip__media--skeleton" />
        <div className="attach-chip__body">
          <span className="attach-chip__name attach-chip__name--skeleton" />
          <span className="attach-chip__meta attach-chip__meta--skeleton" />
        </div>
      </div>
    );
  }

  const clickable = !isLocal && !!fileId;

  return (
    <div
      className={[
        'attach-chip',
        compact ? 'attach-chip--compact' : '',
        clickable ? 'attach-chip--clickable' : '',
        isImage ? 'attach-chip--image' : '',
      ].filter(Boolean).join(' ')}
      onClick={clickable ? handleDownload : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleDownload();
        }
      } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? `Download ${name}` : name}
    >
      <div className="attach-chip__media">
        {isImage && thumb ? (
          <img src={thumb} alt={name} className="attach-chip__thumb" loading="lazy" />
        ) : (
          <i
            className={`fas ${isImage ? 'fa-image' : kindInfo.icon} attach-chip__icon`}
            style={{ color: isImage ? '#0ea5e9' : kindInfo.color }}
            aria-hidden="true"
          />
        )}
      </div>
      <div className="attach-chip__body">
        <span className="attach-chip__name" title={name}>{name}</span>
        {size != null && <span className="attach-chip__meta">{formatSize(size)}</span>}
      </div>
      {onRemove && (
        <button
          type="button"
          className="attach-chip__remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={`${ta('remove')} ${name}`}
          title={ta('remove')}
        >
          <i className="fas fa-times" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

AttachmentChip.propTypes = {
  // Modo local: instancia File para enviar
  file: PropTypes.object,
  // Modo remoto: _id del file en MultimediaService
  fileId: PropTypes.string,
  // Si se provee, muestra el boton de remover (modo redaccion)
  onRemove: PropTypes.func,
  // Variante mas chica (para listings densos)
  compact: PropTypes.bool,
};

export default AttachmentChip;
